import type { LinearRGB } from '../../../core/color';
import { createRng } from '../../../core/rng';
import type { Vec2 } from '../../../geo/types';
import { SURFACE } from '../../mesh/attributes';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { orientedFace, planPoint, prismSolid, ringPoints } from '../solids';
import { buildCrownTemplate } from '../../country/trees';

/** Individually placed fruit trees stand on the local ground, with collidable trunks. */
export function buildConventTrees(
  builder: MeshBuilder,
  ground: VertexStyle,
  garden: VertexStyle,
  frame: Frame,
  colors: { timber: LinearRGB; foliage: LinearRGB },
  terrain: (p: Vec2) => number,
  points: readonly Vec2[],
  name: string
): void {
  const crowns = [0, 1, 2].map((variant) =>
    buildCrownTemplate(createRng(1538).fork(`${name}-crown-${variant}`), ground.edgeId, variant)
  );
  points.forEach((p, i) => {
    prismSolid(
      builder,
      { ...ground, color: colors.timber, surface: SURFACE.wood },
      frame,
      ringPoints(p, 0.15, 5, 0),
      terrain(p) + 0.03,
      terrain(p) + 2.6,
      { top: true, bottom: false }
    );
    const crown = crowns[i % 3]!,
      position = crown.getAttribute('position'),
      index = crown.getIndex()!,
      centre = planPoint(p[0], terrain(p) + 3.45, p[1]),
      scale = 1 + (i % 3) * 0.08;
    for (let t = 0; t < index.count; t += 3) {
      const points = [0, 1, 2].map((k) => {
        const n = index.getX(t + k);
        return [
          centre[0] + position.getX(n) * scale,
          centre[1] + position.getY(n) * scale,
          centre[2] + position.getZ(n) * scale,
        ] as const;
      });
      orientedFace(
        builder,
        { ...garden, color: colors.foliage, surface: SURFACE.foliage },
        frame,
        points,
        [
          points.reduce((s, q) => s + q[0] / 3, 0) - centre[0],
          points.reduce((s, q) => s + q[1] / 3, 0) - centre[1],
          points.reduce((s, q) => s + q[2] / 3, 0) - centre[2],
        ]
      );
    }
  });
  crowns.forEach((crown) => {
    crown.dispose();
  });
}
